import { Link as ReactLink, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Modal,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  canUserManageSubscription,
  getSubscriptionModalContent,
} from '../../helpers/subscriptions';
import useFreeTrialStatus from '../../hooks/subscriptions/useFreeTrialStatus';
import useGetSubscription from '../../hooks/subscriptions/useGetSubscription';
import { ROUTES } from '../../constants/global';
import useSubscriptionModal from '../../hooks/subscriptions/useSubscriptionModal';
import useUser from '../../hooks/global/useUser';
import { LoadedUser } from '../../interfaces';

function SubscriptionModalPopup() {
  const location = useLocation();
  const isDesktop = useMediaQuery('(min-width:900px)');

  const { dismissed, closeSubscriptionModal } = useSubscriptionModal();

  const { user } = useUser<LoadedUser>();
  const { data: { paymentCustomer = null, subscription = null } = {} } =
    useGetSubscription();
  const { daysRemaining, inFreeTrial } = useFreeTrialStatus();

  const onClose = () => {
    closeSubscriptionModal();
  };

  const canManageSubscription = canUserManageSubscription(user);

  const modalContent = getSubscriptionModalContent({
    daysRemaining,
    inFreeTrial: !!inFreeTrial,
    paymentCustomer,
    subscription,
    canManageSubscription,
  });

  if (!modalContent || location.pathname === ROUTES.subscription) return null;

  const style: SxProps<Theme> = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isDesktop ? 500 : '90%',
    bgcolor: 'background.paper',
    borderRadius: '5px',
    boxShadow: 24,
    p: 4,
    boxSizing: 'border-box',
  };

  const handleClose = (reason: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Modal
      open={!!modalContent && !dismissed}
      onClose={(_, reason) => handleClose(reason)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box sx={style} display="flex" flexDirection="column" gap={1.5}>
        <Typography
          id="modal-modal-title"
          variant="h2"
          fontSize={42}
          marginX="auto"
          fontWeight="600"
          textAlign="center"
          marginBottom={2.5}>
          {modalContent.header}
        </Typography>
        <Typography variant="h1">{modalContent.title}</Typography>
        <Typography variant="body1" id="modal-modal-description">
          {modalContent.description}
        </Typography>
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          gap={2}
          marginTop={2.5}>
          {!!modalContent.positiveButtonText && (
            <Button
              component={ReactLink}
              to={ROUTES.subscription}
              onClick={onClose}
              fullWidth
              variant="contained"
              sx={{
                paddingBlock: 1.5,
              }}>
              {modalContent.positiveButtonText}
            </Button>
          )}
          {!!modalContent.negativeButtonText && (
            <Button
              variant="text"
              color="inherit"
              onClick={onClose}
              sx={{
                width: 'fit-content',
                marginX: 'auto',
                ':hover': {
                  bgcolor: 'background.paper',
                },
              }}>
              <Typography
                variant="body2"
                fontWeight="600"
                sx={{ textDecoration: 'underline' }}>
                {modalContent.negativeButtonText}
              </Typography>
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
}

export default SubscriptionModalPopup;
